interface PaymentData {
  vatIncludedCountries: string[]
  supportedCurrencies: Currency[]
  pricePlans: PricePlan[]
  planHierarchy: PricePlanName[]
  paymentMethod: CardDetails | null
}

export const usePaymentStore = defineStore('payment', {
  state: (): PaymentData => ({
    vatIncludedCountries: ['CH', 'LI'],
    supportedCurrencies: ['EUR', 'CHF'],
    pricePlans: [],
    // The price plans we offer, ordered from cheapest to most expensive
    planHierarchy: ['Trial', 'Individual', 'Team', 'Pro', 'Scale'],
    paymentMethod: null,
  }),
  getters: {
    getSortedCurrencies: state => {
      const systemStore = useSystemStore()
      const { platform } = storeToRefs(systemStore)
      return platform.value === 'CH' ? state.supportedCurrencies.toReversed() : state.supportedCurrencies
    },
    getBillingFromPaymentMethod: state => {
      return state.paymentMethod?.billing
    },
  },
  actions: {
    async createPaymentIntent(
      paymentIntentPayload: PaymentIntentPayload | DirectPaymentIntentPayload,
      signal?: AbortSignal
    ) {
      const businessStore = useBusinessStore()
      const { paymentRepository } = useLegacyApi()
      if (businessStore.id) {
        return await paymentRepository.createPaymentIntent(businessStore.id, paymentIntentPayload, signal)
      } else {
        return await paymentRepository.createDirectPaymentIntent(paymentIntentPayload, signal)
      }
    },
    async directUpgradeSubscription(
      subscriptionPayload: UpgradeSubscriptionPayload,
      addressData: Address,
      currency: Currency,
      name: string
    ) {
      const userStore = useUserStore()
      const { paymentRepository } = useLegacyApi()
      await paymentRepository.directUpgradeSubscription({
        ...subscriptionPayload,
        ...addressData,
        currency: currency,
        name: name,
        gtcAccepted: true,
      })

      await userStore.fetchUser()
      await userStore.loadUserBusinessData()
    },
    async upgradeSubscription(subscriptionPayload: UpgradeSubscriptionPayload) {
      const { $api } = useNuxtApp()
      const businessStore = useBusinessStore()
      const userStore = useUserStore()

      const updatedPricePlan = await $api(`/api/businesses/${businessStore.id}/subscriptions/upgrade`, {
        method: 'PUT',
        body: subscriptionPayload,
      })

      businessStore.$patch({ pricePlan: updatedPricePlan })
      await userStore.loadUserBusinessData()
    },
    async upgradeTrial(type: string) {
      const businessStore = useBusinessStore()
      const { paymentRepository } = useLegacyApi()
      await paymentRepository.upgradeTrial(businessStore.id, type)
    },
    async getBusinessPricePlans(): Promise<PricePlan[]> {
      const { paymentRepository } = useLegacyApi()

      const pricePlans = await paymentRepository.getPricePlans()

      const namedPricePlans: PricePlan[] = pricePlans
        .filter(plan => !plan.id.startsWith('TRIAL'))
        .map(plan => {
          const inferredName = plan.id.split('_').shift()

          return {
            ...plan,
            name: (inferredName ? inferredName.charAt(0) + inferredName.slice(1).toLowerCase() : '') as PricePlanName,
          }
        })

      this.$patch({ pricePlans: namedPricePlans })

      return namedPricePlans
    },
    async getCurrentPaymentMethod(): Promise<CardDetails | null> {
      const { $api } = useNuxtApp()
      const businessStore = useBusinessStore()
      const paymentMethod = await $api(`/api/businesses/${businessStore.id}/billing/card`)
      this.$patch({ paymentMethod })
      return paymentMethod
    },
    async getActiveSubscription(businessId: string) {
      const { paymentRepository } = useLegacyApi()
      return await paymentRepository.getActiveSubscription(businessId)
    },
    async getCreditsBalance(businessId: string) {
      const { paymentRepository } = useLegacyApi()
      return await paymentRepository.getCreditsBalance(businessId)
    },
  },
})
